import React, { useState, useEffect, useContext } from 'react';

import { SelectForm } from '@components/form/select';
import { getDaysInMonth, parse } from 'date-fns';
import { HrLabel, HrRed, HrParagraph, HrFlexContainer, HrGridContainer } from '@components/styles/hr-styles';
import { isEqual, orderBy } from 'lodash';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { useTranslation } from 'react-i18next';
import { buildSelectOptionsString } from '../../../utils/form';
import { DAYS, YEARS, MONTHS, formatDateObject, isOlderYears, parseDate } from '../../utilities/date';
import { DisplaySettingsContext } from '../../../postulation_form/store/display-settings';

export const SelectDate = ({
  date,
  title,
  id,
  control,
  formState,
  validate,
  getValues,
  setValue,
  setError,
  clearErrors,
  errorPosition,
  allow16yearsPostulants
}) => {
  const [days, setdays] = useState([]);
  const [t] = useTranslation();
  const { errors } = formState;
  const inputDay = `${id}.day`;
  const inputMonth = `${id}.month`;
  const inputYear = `${id}.year`;

  const _validate = {};
  const dateObs = useWatch({ control, name: id });
  const yearStart = 1944;
  const [dateObj, setDateObj] = useState({});
  if (validate.required) {
    _validate.required = 'campo requerido';
  }

  useEffect(() => {
    if (date) {
      const _date = parseDate(date);
      if (_date) {
        setDateObj({
          day: _date.getDate(),
          month: _date.getMonth(),
          year: _date.getFullYear()
        });
        setValue(inputDay, _date.getDate());
        setValue(inputMonth, _date.getMonth());
        setValue(inputYear, _date.getFullYear());
      }
    }
  }, [date]);

  useEffect(() => {
    if (dateObs && !isEqual(dateObs, dateObj)) {
      if (dateObs.day && dateObs.month >= 0 && dateObs.year) {
        setDateObj({
          day: dateObs.day,
          month: dateObs.month,
          year: dateObs.year
        });
      }
    }
  }, [dateObs]);

  const getIdError = () => {
    const error = errors[id] || {};
    let idError = id;
    const { day, month, year } = error;
    if (day?.type === 'required') {
      idError = inputDay;
    } else if (month?.type === 'required') {
      idError = inputMonth;
    } else if (year?.type === 'required') {
      idError = inputYear;
    }
    return idError;
  };

  useEffect(() => {
    if (dateObj) {
      if (dateObj.day && dateObj.month >= 0 && dateObj.year) {
        const dateFormat = formatDateObject(dateObj);
        const maxAge = allow16yearsPostulants ? 16 : 18;
        const isAdult = isOlderYears(dateObj, maxAge);
        const message = `No puede ser menor de ${maxAge} años.`;
        if (isAdult) {
          setValue('born', dateFormat);
          clearErrors(id);
        } else {
          setError(`${id}`, { message });
        }
      }
    }
  }, [dateObj]);

  const days31Default = buildSelectOptionsString(DAYS);

  useEffect(() => {
    setdays(days31Default);
  }, []);

  const changeYear = (data) => {
    const monthSelect = getValues(`${id}.month`) || 1;
    const daysInMonth = getDaysInMonth(new Date(data.value, monthSelect));
    const _days = [...days31Default];
    _days.length = daysInMonth;
    setdays(_days);
  };

  const changeMonth = (data) => {
    const yearSelect = getValues(`${id}.year`) || yearStart;
    const daysInMonth = getDaysInMonth(new Date(yearSelect, data.value));
    const _days = [...days31Default];
    _days.length = daysInMonth;
    setdays(_days);
  };

  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

  return (
    <HrFlexContainer gap='8px'>
      {title && (
        <HrFlexContainer justifyContent='space-between'>
          <HrLabel fontSizeSetting={fontSizeSetting} htmlFor={id} isRequired={validate.required} userSelect='none'  color={darkMode ? 'white' : ''}>
            {title}
          </HrLabel>
        </HrFlexContainer>
      )}
      <HrGridContainer columns={3} columnGap='8px'>
        <SelectForm
          id={inputDay}
          width='auto'
          placeholder={t('general.hiringroom.date.day')}
          data={days}
          control={control}
          formState={formState}
          isSearchable
          validate={_validate}
          defaultValue={null}
          direction='row'
        />
        <SelectForm
          width='auto'
          id={inputMonth}
          placeholder={t('general.hiringroom.date.month')}
          data={MONTHS}
          control={control}
          formState={formState}
          isSearchable
          validate={_validate}
          // defaultValue={null}
          onChangee={(data) => {
            changeMonth(data);
          }}
        />
        <SelectForm
          id={inputYear}
          width='auto'
          placeholder={t('general.hiringroom.date.year')}
          data={YEARS('desc')}
          control={control}
          formState={formState}
          isSearchable
          validate={_validate}
          // defaultValue={null}
          onChangee={(data) => {
            changeYear(data);
          }}
        />
      </HrGridContainer>
      {
        // <HrParagraph color={HrRed}>errors[id]?.message</HrParagraph>
        <ErrorMessage
          errors={errors}
          name={getIdError()}
          render={({ message }) => <HrParagraph color={HrRed}>{message}</HrParagraph>}
        />
      }
    </HrFlexContainer>
  );
};

export const SelectYear = ({ title, placeholder, id, control, formState, validate, order, disabled }) => {
  return (
    <HrFlexContainer gap='8px' flexDirection='column'>
      {title && (
        <HrFlexContainer justifyContent='space-between'>
          <HrLabel htmlFor={id} isRequired={validate.required} userSelect='none'>
            {title}
          </HrLabel>
        </HrFlexContainer>
      )}
      <SelectForm
        id={id}
        placeholder={placeholder}
        data={orderBy(YEARS(), 'label', order)}
        control={control}
        formState={formState}
        isSearchable
        validate={validate}
        disabled={disabled}
      />
    </HrFlexContainer>
  );
};
SelectYear.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  control: PropTypes.object,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  validate: PropTypes.object,
  formState: PropTypes.object,
  direction: PropTypes.string,
  order: PropTypes.string,
  placeholder: PropTypes.string
};

SelectYear.defaultProps = {
  id: '',
  title: null,
  control: {},
  onClick: () => {},
  validate: {},
  formState: { errors: {} },
  disabled: false,
  direction: 'column',
  order: 'desc',
  placeholder: 'Año' // no se como poner los textos traducidos
};

export const SelectDateCustom = ({ title, ids, control, formState, validate, disabled }) => {
  const [t] = useTranslation();
  const { darkMode, fontSizeSetting} = useContext(DisplaySettingsContext);
  return (
    <HrFlexContainer gap='8px'>
      {title && (
        <HrFlexContainer justifyContent='space-between'>
          <HrLabel fontSizeSetting={fontSizeSetting} color={darkMode ? 'white' : ''} htmlFor={ids.month} isRequired={validate.required} userSelect='none'>
            {title}
          </HrLabel>
        </HrFlexContainer>
      )}
      <HrGridContainer columns={2} columnGap='8px'>
        <SelectForm
          width='auto'
          id={ids.month}
          placeholder={t('general.hiringroom.date.month')}
          data={MONTHS}
          control={control}
          formState={formState}
          isSearchable
          validate={validate}
          defaultValue={null}
          disabled={disabled}
          errorPosition='bottom'
        />
        <SelectForm
          id={ids.year}
          width='auto'
          placeholder={t('general.hiringroom.date.year')}
          data={YEARS('desc')}
          control={control}
          formState={formState}
          isSearchable
          validate={validate}
          defaultValue={null}
          disabled={disabled}
          errorPosition='bottom'
        />
      </HrGridContainer>
    </HrFlexContainer>
  );
};

SelectDateCustom.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  control: PropTypes.object,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  validate: PropTypes.object,
  formState: PropTypes.object,
  direction: PropTypes.string,
  order: PropTypes.string,
  placeholder: PropTypes.string
};

SelectDateCustom.defaultProps = {
  id: '',
  title: null,
  control: {},
  onClick: () => {},
  validate: {},
  formState: { errors: {} },
  disabled: false,
  direction: 'column',
  order: 'desc',
  placeholder: 'Mes'
};
